import { FC, PropsWithChildren, ReactNode, useRef } from "react";
import { SWRResponse } from "swr";
import { useI18n } from "../../hooks/useTranslations";
import { CustomError, getErrorMessage } from "../../http/errors";
import { Version, Versions } from "../../types";
import Form from "../ui/Form";
import CheckBox from "./CheckBox";
import ErrorText from "./ErrorText";
import FormField from "./FormField";
import Input from "./Input";
import LoadingSpinner from "./LoadingSpinner";
import styles from "./VersionDialog.module.css";

type VersionDialogProps<T extends Version> = {
  entityId: string;
  lastVersion: number;
  version: Version;
  getEntityVersionHook: (
    entityId: string,
    versionNumber: number,
    signal: AbortSignal,
  ) => SWRResponse<Versions<T> | null, CustomError, any>;
  renderForm: (headers: string[], data: Versions<T>) => ReactNode;
};

const VersionDialog = <T extends Version>({
  entityId,
  lastVersion,
  version,
  getEntityVersionHook,
  renderForm,
}: VersionDialogProps<T>) => {
  const i18n = useI18n();
  const aborter = useRef(new AbortController());

  const { data, error } = getEntityVersionHook(entityId, version.versionNumber, aborter.current.signal);

  const headers = [
    `${i18n.t.common.version} ${data?.current.versionNumber}`,
    lastVersion === data?.next.versionNumber
      ? i18n.t.common.currentVersion
      : `${i18n.t.common.version} ${data?.next.versionNumber}`,
  ];

  return (
    <Form
      itemCreationInfo={{
        modifiedAt: data?.next.createdAt,
        editor: data?.next.author,
      }}
    >
      {!!error && <ErrorText text={getErrorMessage(i18n, error)} />}
      {!!data && renderForm(headers, data)}
      {!error && !data && (
        <div className={styles.spinner}>
          <LoadingSpinner />
        </div>
      )}
    </Form>
  );
};

type VersionDialogTableProps = PropsWithChildren<{
  headers?: string[];
}>;

const VersionDialogTable: FC<VersionDialogTableProps> = ({ children, headers }) => (
  <table className={styles["version-table"]}>
    {headers && headers.length > 0 ? (
      <thead>
        <tr>
          {headers.map((x, i) => (
            <th align="left" key={`v_t_h_${i}`}>
              {x}
            </th>
          ))}
        </tr>
      </thead>
    ) : undefined}
    <tbody>{children}</tbody>
  </table>
);

interface VersionDialogFieldProps {
  label: string;
  value: string | number | boolean | React.ReactNode | null | undefined;
  edited: string | number | boolean | React.ReactNode | null | undefined;
}

const VersionDialogField: React.FC<VersionDialogFieldProps> = ({ label, value, edited }) => {
  // Resolve the type from first defined value
  const resolved = value !== null && value !== undefined ? value : edited;
  const type = typeof resolved;

  // Render for string or number — two disabled inputs
  if (type === "string" || type === "number") {
    return (
      <>
        <td>
          <FormField label={label}>
            <Input value={typeof value === "string" || typeof value === "number" ? value : ""} disabled />
          </FormField>
        </td>
        <td>
          <FormField label={label}>
            <Input value={typeof edited === "string" || typeof edited === "number" ? edited : ""} disabled />
          </FormField>
        </td>
      </>
    );
  }

  // Render for boolean — two disabled checkboxes
  if (type === "boolean") {
    return (
      <>
        <td>
          <FormField label={label}>
            <CheckBox defaultChecked={!!value} disabled />
          </FormField>
        </td>
        <td>
          <FormField label={label}>
            <CheckBox defaultChecked={!!edited} disabled />
          </FormField>
        </td>
      </>
    );
  }

  // For ReactNode or undefined/null — just render children, show empty string if nullish
  return (
    <>
      <td>
        <FormField label={label}>{value}</FormField>
      </td>
      <td>
        <FormField label={label}>{edited}</FormField>
      </td>
    </>
  );
};

VersionDialog.Table = VersionDialogTable;
VersionDialog.Field = VersionDialogField;

export default VersionDialog;
